import { useEffect, useRef, useState } from "react";

const benefits = [
  {
    number: "01",
    icon: "ri-seedling-line",
    title: "Transformar el trauma en legado",
    desc: "Carlos no solo sobrevivió; convirtió cada hora de oscuridad en una lección de vida. Este libro te enseña que el dolor no tiene que ser el final de tu historia, sino el comienzo de tu mayor capítulo.",
  },
  {
    number: "02",
    icon: "ri-heart-pulse-line",
    title: "Mantener la fe cuando la lógica falla",
    desc: "Cuando los títulos, los contratos y la razón no tienen respuestas, ¿qué te sostiene? Carlos descubrió que la fe no es la ausencia de miedo, sino la decisión de seguir de pie a pesar de él.",
  },
  {
    number: "03",
    icon: "ri-door-open-line",
    title: "El poder liberador del perdón",
    desc: "Perdonar no es olvidar ni justificar. Es elegir no cargar el peso de la amargura. Carlos aprendió que el perdón es el acto más valiente que un ser humano puede realizar, y el más liberador.",
  },
];

export default function PorQueSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="por-que" ref={ref} className="py-28 md:py-40 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-16">

        {/* Header */}
        <div
          className={`text-center mb-6 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: "#D4AF37", letterSpacing: "4px" }}>
            Valor Universal
          </p>
          <h2
            className="font-bold mb-4"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2rem, 4vw, 3rem)",
              color: "#002D62",
              lineHeight: 1.2
            }}
          >
            Una brújula para tus propias luchas
          </h2>
          {/* Gold divider */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="h-px w-16" style={{ background: "#D4AF37" }} />
            <div className="w-2 h-2 rounded-full" style={{ background: "#D4AF37" }} />
            <div className="h-px w-16" style={{ background: "#D4AF37" }} />
          </div>
          <p
            className="max-w-2xl mx-auto"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "1.05rem",
              color: "#666",
              lineHeight: 1.8
            }}
          >
            Aunque pocos viven un secuestro físico, <strong style={{ color: "#002D62" }}>todos enfrentamos "secuestros" emocionales</strong>: crisis, pérdidas, traiciones, momentos en que la vida nos arrebata el control. Este libro es tu mapa de salida.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {benefits.map((benefit, index) => (
            <div
              key={benefit.number}
              className={`group rounded-2xl p-10 transition-all duration-700 cursor-default ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
              style={{
                border: "1px solid #002D62",
                transitionDelay: `${0.2 + index * 0.15}s`
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLDivElement).style.background = "#002D62";
                (e.currentTarget as HTMLDivElement).style.transform = "translateY(-6px)";
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLDivElement).style.background = "transparent";
                (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
              }}
            >
              {/* Number */}
              <p
                className="font-bold leading-none mb-6 transition-colors duration-300 group-hover:opacity-60"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "5rem",
                  color: "#D4AF37",
                  opacity: 0.35
                }}
              >
                {benefit.number}
              </p>

              {/* Icon */}
              <div className="w-12 h-12 flex items-center justify-center rounded-full mb-5 transition-colors duration-300" style={{ background: "rgba(0,45,98,0.08)" }}>
                <i className={`${benefit.icon} text-xl`} style={{ color: "#002D62" }} />
              </div>

              {/* Title */}
              <h3
                className="font-bold mb-4 transition-colors duration-300"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "1.25rem",
                  color: "#002D62",
                  lineHeight: 1.3
                }}
              >
                {benefit.title}
              </h3>

              {/* Description */}
              <p
                className="leading-relaxed transition-colors duration-300"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.95rem",
                  color: "#555",
                  lineHeight: 1.8
                }}
              >
                {benefit.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Central Quote */}
        <div
          className={`max-w-3xl mx-auto text-center transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: "0.7s" }}
        >
          <div
            className="relative px-10 py-10 rounded-2xl"
            style={{
              background: "linear-gradient(135deg, rgba(0,45,98,0.04), rgba(212,175,55,0.06))",
              border: "1px solid rgba(212,175,55,0.3)"
            }}
          >
            <span
              className="absolute -top-6 left-8 text-6xl leading-none"
              style={{ color: "#D4AF37", fontFamily: "'Playfair Display', serif" }}
            >
              &ldquo;
            </span>
            <p
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.1rem, 2vw, 1.4rem)",
                color: "#002D62",
                fontStyle: "italic",
                lineHeight: 1.7
              }}
            >
              Aunque pocos viven un secuestro físico, todos enfrentamos &lsquo;secuestros&rsquo; emocionales. Este libro es tu mapa de salida.
            </p>
            <span
              className="absolute -bottom-6 right-8 text-6xl leading-none"
              style={{ color: "#D4AF37", fontFamily: "'Playfair Display', serif" }}
            >
              &rdquo;
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
